/* -------------------------------------------------------------------------- */
/*  Model Data — Electric & Emerging Brands (5 brands, 12 models)             */
/*  UX-43: Model Page Template                                                */
/* -------------------------------------------------------------------------- */

import { type ModelData } from './model-data-types';

export const electricModels: ModelData[] = [
  /* ------------------------------------------------------------------------ */
  /*  RIVIAN (3)                                                              */
  /* ------------------------------------------------------------------------ */
  {
    slug: 'r1t',
    brandSlug: 'rivian',
    name: 'R1T',
    bodyType: 'truck',
    years: [2022, 2023, 2024, 2025],
    startingPrice: 7390000,
    description:
      'The Rivian R1T is the electric adventure truck that redefined what a pickup can be. With quad-motor all-wheel drive producing up to 835 horsepower, the R1T conquers any terrain while delivering over 300 miles of range. Its innovative gear tunnel provides lockable storage for gear and equipment. The cabin features a minimalist design with sustainable materials, a panoramic glass roof, and a 16-inch infotainment display. Whether towing a boat or navigating a rocky trail, the R1T blends rugged capability with zero-emission driving in a way no other truck can match.',
    highlights: [
      'Quad-motor AWD producing up to 835 hp for unmatched off-road capability',
      'Over 300 miles of EPA-estimated range on a single charge',
      'Innovative Gear Tunnel lockable storage compartment',
      'Adjustable air suspension with over 14 inches of ground clearance',
    ],
    specs: [
      { label: 'Motor', value: 'Quad Permanent Magnet' },
      { label: 'Horsepower', value: '835 hp (Max Pack)' },
      { label: '0-60 MPH', value: '3.0 seconds' },
      { label: 'Range', value: '314 miles' },
      { label: 'Towing', value: '11,000 lbs' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 85,
    seo: {
      title: 'Used Rivian R1T for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Rivian R1T electric adventure trucks. Quad-motor AWD, 300+ mile range, and legendary off-road capability.',
    },
  },
  {
    slug: 'r1s',
    brandSlug: 'rivian',
    name: 'R1S',
    bodyType: 'suv',
    years: [2022, 2023, 2024, 2025],
    startingPrice: 7890000,
    description:
      'The Rivian R1S is the three-row electric SUV built for families who refuse to compromise on adventure. Sharing its skateboard platform with the R1T, the R1S delivers the same quad-motor performance and impressive range in a spacious seven-passenger package. Its adjustable air suspension and waterproof interior make it equally comfortable on highways and river crossings. The R1S features a driver-focused cabin with premium materials, a panoramic sunroof, and advanced driver assistance systems. For families seeking an electric SUV that can truly go anywhere, the R1S stands in a class of its own.',
    highlights: [
      'Seven-passenger three-row seating with flat-folding capability',
      'Quad-motor AWD with up to 835 hp and adjustable air suspension',
      'Over 300 miles of range with DC fast charging support',
      'Waterproof interior and 14+ inches of ground clearance for true off-roading',
    ],
    specs: [
      { label: 'Motor', value: 'Quad Permanent Magnet' },
      { label: 'Horsepower', value: '835 hp (Max Pack)' },
      { label: '0-60 MPH', value: '3.0 seconds' },
      { label: 'Range', value: '321 miles' },
      { label: 'Seating', value: '7 passengers' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 72,
    seo: {
      title: 'Used Rivian R1S for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Rivian R1S electric SUVs. Three-row seating, quad-motor AWD, and adventure-ready capability for the whole family.',
    },
  },
  {
    slug: 'r2',
    brandSlug: 'rivian',
    name: 'R2',
    bodyType: 'suv',
    years: [2025],
    startingPrice: 4500000,
    description:
      'The Rivian R2 is the more accessible entry point into the Rivian ecosystem, bringing the brand\'s adventure-focused DNA to a compact crossover format. Designed to compete in the heart of the EV market, the R2 offers an estimated 300 miles of range, dual-motor all-wheel drive, and a suite of advanced technology features. Its smaller footprint makes it ideal for urban environments while retaining genuine off-road capability. The R2 features a modern interior with sustainable materials, intuitive controls, and the same commitment to quality that defines its larger siblings.',
    highlights: [
      'Compact crossover design with genuine off-road capability',
      'Estimated 300+ miles of range at an accessible price point',
      'Dual-motor AWD standard with advanced traction management',
      'Sustainable interior materials and minimalist design language',
    ],
    specs: [
      { label: 'Motor', value: 'Dual Permanent Magnet' },
      { label: 'Horsepower', value: '500 hp (est.)' },
      { label: '0-60 MPH', value: '4.0 seconds (est.)' },
      { label: 'Range', value: '300+ miles (est.)' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 12,
    seo: {
      title: 'Used Rivian R2 for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Rivian R2 compact electric SUVs. Adventure-ready capability in an accessible, efficient package.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /*  LUCID (2)                                                               */
  /* ------------------------------------------------------------------------ */
  {
    slug: 'air',
    brandSlug: 'lucid',
    name: 'Air',
    bodyType: 'sedan',
    years: [2022, 2023, 2024, 2025],
    startingPrice: 6990000,
    description:
      'The Lucid Air is the luxury electric sedan that shattered range records and redefined what premium EVs can achieve. With up to 516 miles of EPA-estimated range in the Grand Touring trim, the Air offers more range than any other production electric vehicle. Its space-concept interior delivers limousine-level rear legroom in a midsize footprint, while the Dream Drive Pro system provides advanced autonomous driving capabilities. Handcrafted in Arizona, the Air features a glass canopy roof, 34-speaker Surreal Sound system, and up to 1,234 horsepower in the Sapphire trim. It is electric luxury without compromise.',
    highlights: [
      'Up to 516 miles of EPA-estimated range, leading all production EVs',
      'Up to 1,234 hp in Sapphire trim with sub-2-second 0-60 capability',
      'Space-concept interior with class-leading rear legroom',
      '34-speaker Surreal Sound Pro audio system by Dolby Atmos',
    ],
    specs: [
      { label: 'Motor', value: 'Dual/Tri Permanent Magnet' },
      { label: 'Horsepower', value: 'Up to 1,234 hp' },
      { label: '0-60 MPH', value: '1.89 seconds (Sapphire)' },
      { label: 'Range', value: 'Up to 516 miles' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 65,
    seo: {
      title: 'Used Lucid Air for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Lucid Air luxury electric sedans. Record-setting range, stunning performance, and handcrafted luxury.',
    },
  },
  {
    slug: 'gravity',
    brandSlug: 'lucid',
    name: 'Gravity',
    bodyType: 'suv',
    years: [2025],
    startingPrice: 7990000,
    description:
      'The Lucid Gravity is the highly anticipated luxury electric SUV that extends the Air\'s groundbreaking technology into a three-row family format. Built on Lucid\'s proprietary electric architecture, the Gravity promises over 440 miles of range while seating up to seven passengers in spacious comfort. Its low center of gravity and advanced air suspension deliver sports-car-like handling despite its generous dimensions. The Gravity features the same glass canopy roof and space-concept cabin philosophy as the Air, with added versatility through configurable second and third rows. It represents a new standard in electric luxury SUV design and capability.',
    highlights: [
      'Over 440 miles of estimated range on Lucid electric architecture',
      'Three-row seating for up to seven passengers with generous cargo space',
      'Air suspension with adaptive damping for sports-car-like handling',
      'Glass canopy roof and space-concept interior design',
    ],
    specs: [
      { label: 'Motor', value: 'Dual Permanent Magnet' },
      { label: 'Horsepower', value: '800+ hp (est.)' },
      { label: '0-60 MPH', value: '3.5 seconds (est.)' },
      { label: 'Range', value: '440+ miles (est.)' },
      { label: 'Seating', value: '7 passengers' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 18,
    seo: {
      title: 'Used Lucid Gravity for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Lucid Gravity luxury electric SUVs. Three-row seating, 440+ mile range, and post-luxury design.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /*  SMART (2)                                                               */
  /* ------------------------------------------------------------------------ */
  {
    slug: 'hashtag-1',
    brandSlug: 'smart',
    name: '#1',
    bodyType: 'suv',
    years: [2023, 2024, 2025],
    startingPrice: 2990000,
    description:
      'The Smart #1 marks the brand\'s transformation from a city-car maker into a credible compact electric SUV contender. Developed in partnership with Geely and built on the SEA architecture, the #1 offers up to 270 miles of range, a spacious interior that belies its compact dimensions, and a tech-forward cabin with a 12.8-inch infotainment display. The BRABUS variant delivers 428 horsepower and dual-motor all-wheel drive for genuine performance credentials. With its bold design, peppy handling, and zero emissions, the Smart #1 is the ideal urban electric crossover for style-conscious drivers.',
    highlights: [
      'Up to 270 miles of range on the advanced SEA electric platform',
      'BRABUS variant with 428 hp and dual-motor AWD performance',
      '12.8-inch central infotainment display with connected services',
      'Compact dimensions with surprisingly spacious interior packaging',
    ],
    specs: [
      { label: 'Motor', value: 'Single/Dual Permanent Magnet' },
      { label: 'Horsepower', value: '272 hp / 428 hp (BRABUS)' },
      { label: '0-60 MPH', value: '3.9 seconds (BRABUS)' },
      { label: 'Range', value: 'Up to 270 miles' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'RWD / AWD (BRABUS)' },
    ],
    inventoryCount: 28,
    seo: {
      title: 'Used Smart #1 for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Smart #1 electric crossovers. Compact, stylish, and efficient urban electric mobility.',
    },
  },
  {
    slug: 'hashtag-3',
    brandSlug: 'smart',
    name: '#3',
    bodyType: 'suv',
    years: [2024, 2025],
    startingPrice: 3290000,
    description:
      'The Smart #3 is the coupe-styled electric crossover that brings added flair to the brand\'s reinvented lineup. Larger than the #1, the #3 features a fastback silhouette that combines sporty aesthetics with practical versatility. Built on the same SEA platform, it offers comparable range and performance while adding more rear-seat space and cargo capacity. The interior features premium materials, a floating center console, and a head-up display. With its striking design and responsive electric powertrain, the Smart #3 appeals to drivers who want their electric crossover to make a visual statement as bold as its environmental one.',
    highlights: [
      'Coupe-styled crossover design with fastback silhouette',
      'Larger cabin than #1 with improved rear-seat and cargo space',
      'Advanced SEA platform with up to 270 miles of range',
      'Premium interior with head-up display and floating console',
    ],
    specs: [
      { label: 'Motor', value: 'Single/Dual Permanent Magnet' },
      { label: 'Horsepower', value: '272 hp / 428 hp (BRABUS)' },
      { label: '0-60 MPH', value: '3.9 seconds (BRABUS)' },
      { label: 'Range', value: 'Up to 270 miles' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'RWD / AWD (BRABUS)' },
    ],
    inventoryCount: 22,
    seo: {
      title: 'Used Smart #3 for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned Smart #3 electric coupe-crossovers. Bold design and efficient electric performance.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /*  VINFAST (3)                                                             */
  /* ------------------------------------------------------------------------ */
  {
    slug: 'vf-8',
    brandSlug: 'vinfast',
    name: 'VF 8',
    bodyType: 'suv',
    years: [2023, 2024, 2025],
    startingPrice: 4690000,
    description:
      'The VinFast VF 8 is a midsize all-electric SUV that brings Vietnamese engineering ambition to the global stage. Offering two battery options with up to 264 miles of range, the VF 8 seats five in a spacious cabin appointed with vegan leather and a 15.6-inch infotainment screen. Standard features include advanced driver assistance, a panoramic sunroof, and heated front seats. VinFast backs the VF 8 with an industry-leading 10-year vehicle warranty, making it one of the most compelling value propositions in the electric SUV segment. Its competitive pricing and generous equipment list make electric driving accessible to more families.',
    highlights: [
      'Midsize electric SUV with up to 264 miles of range',
      'Industry-leading 10-year, 125,000-mile vehicle warranty',
      '15.6-inch infotainment display with standard advanced driver assistance',
      'Competitive pricing with generous standard equipment list',
    ],
    specs: [
      { label: 'Motor', value: 'Dual Permanent Magnet' },
      { label: 'Horsepower', value: '349 hp' },
      { label: '0-60 MPH', value: '5.3 seconds' },
      { label: 'Range', value: 'Up to 264 miles' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 38,
    seo: {
      title: 'Used VinFast VF 8 for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned VinFast VF 8 electric SUVs. Generous features, 10-year warranty, and competitive pricing.',
    },
  },
  {
    slug: 'vf-9',
    brandSlug: 'vinfast',
    name: 'VF 9',
    bodyType: 'suv',
    years: [2023, 2024, 2025],
    startingPrice: 7590000,
    description:
      'The VinFast VF 9 is a full-size three-row electric SUV designed to compete with premium offerings at a more accessible price point. With seating for up to seven passengers and over 260 miles of range, the VF 9 combines family practicality with electric efficiency. Its commanding presence features a bold grille design and LED lighting signature, while the interior offers quilted vegan leather, a 15.6-inch infotainment screen, and a second-row entertainment display. The VF 9 comes standard with all-wheel drive, air suspension, and a comprehensive suite of safety features, all backed by VinFast\'s exceptional 10-year warranty.',
    highlights: [
      'Full-size three-row electric SUV seating up to seven passengers',
      'Standard air suspension and all-wheel drive for a smooth ride',
      'Premium interior with quilted vegan leather and dual displays',
      '10-year, 125,000-mile warranty with comprehensive coverage',
    ],
    specs: [
      { label: 'Motor', value: 'Dual Permanent Magnet' },
      { label: 'Horsepower', value: '402 hp' },
      { label: '0-60 MPH', value: '6.5 seconds' },
      { label: 'Range', value: 'Up to 262 miles' },
      { label: 'Seating', value: '7 passengers' },
      { label: 'Drivetrain', value: 'AWD' },
    ],
    inventoryCount: 25,
    seo: {
      title: 'Used VinFast VF 9 for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned VinFast VF 9 three-row electric SUVs. Full-size luxury, 7-passenger seating, and 10-year warranty.',
    },
  },
  {
    slug: 'vf-6',
    brandSlug: 'vinfast',
    name: 'VF 6',
    bodyType: 'suv',
    years: [2024, 2025],
    startingPrice: 3490000,
    description:
      'The VinFast VF 6 is a compact electric crossover that serves as the most affordable entry into the VinFast lineup. Designed for urban mobility with a touch of adventure, the VF 6 delivers an estimated 200 miles of range from its single-motor powertrain. Its compact footprint makes it easy to park and maneuver, while the interior offers a modern design with a large touchscreen, vegan leather seating, and standard advanced driver assistance. The VF 6 represents VinFast\'s commitment to making electric vehicles accessible to a broader audience, combining practical daily driving capability with the brand\'s generous warranty and standard equipment philosophy.',
    highlights: [
      'Most affordable entry into the VinFast electric lineup',
      'Compact crossover design ideal for urban environments',
      'Estimated 200 miles of range for daily driving needs',
      '10-year warranty with comprehensive standard equipment',
    ],
    specs: [
      { label: 'Motor', value: 'Single Permanent Magnet' },
      { label: 'Horsepower', value: '201 hp' },
      { label: '0-60 MPH', value: '7.5 seconds' },
      { label: 'Range', value: '200 miles (est.)' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'FWD' },
    ],
    inventoryCount: 20,
    seo: {
      title: 'Used VinFast VF 6 for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned VinFast VF 6 compact electric crossovers. Affordable electric mobility with generous warranty.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /*  INEOS (2)                                                               */
  /* ------------------------------------------------------------------------ */
  {
    slug: 'grenadier',
    brandSlug: 'ineos',
    name: 'Grenadier',
    bodyType: 'suv',
    years: [2023, 2024, 2025],
    startingPrice: 7150000,
    description:
      'The INEOS Grenadier is a purpose-built, utilitarian 4x4 designed for those who need a vehicle that works as hard as they do. Powered by BMW-sourced 3.0-liter turbocharged inline-six engines in both petrol and diesel variants, the Grenadier features permanent four-wheel drive, beam axles front and rear, and a robust ladder-frame chassis. Its boxy, no-nonsense design maximizes interior space and practicality, with hose-washable rubber floors and external utility mounting points. The cabin balances rugged functionality with surprising comfort, including Recaro seats and a modern infotainment system. The Grenadier is a genuine workhorse for serious off-road enthusiasts.',
    highlights: [
      'BMW-sourced 3.0L twin-turbo inline-six engine with 281 hp',
      'Beam axles front and rear with permanent four-wheel drive',
      'Ladder-frame chassis designed for extreme off-road durability',
      'Hose-washable interior with external utility mounting points',
    ],
    specs: [
      { label: 'Engine', value: '3.0L Twin-Turbo I6 (BMW)' },
      { label: 'Horsepower', value: '281 hp' },
      { label: '0-60 MPH', value: '8.6 seconds' },
      { label: 'Towing', value: '7,716 lbs' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'Permanent 4WD' },
    ],
    inventoryCount: 30,
    seo: {
      title: 'Used INEOS Grenadier for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned INEOS Grenadier 4x4s. Purpose-built off-road capability with BMW-sourced powertrain.',
    },
  },
  {
    slug: 'quartermaster',
    brandSlug: 'ineos',
    name: 'Quartermaster',
    bodyType: 'truck',
    years: [2024, 2025],
    startingPrice: 7350000,
    description:
      'The INEOS Quartermaster is the double-cab pickup variant of the Grenadier, extending the platform with a functional cargo bed while retaining all of the original\'s legendary off-road capability. Sharing the same BMW-sourced 3.0-liter twin-turbo inline-six, permanent four-wheel drive, and beam-axle suspension, the Quartermaster adds genuine load-carrying utility to the formula. Its extended wheelbase improves highway stability while the steel cargo bed handles heavy payloads with ease. The cabin mirrors the Grenadier\'s blend of rugged materials and modern technology. For those who need a working truck that can traverse any terrain, the Quartermaster delivers without pretense.',
    highlights: [
      'Double-cab pickup with functional steel cargo bed',
      'Same BMW 3.0L twin-turbo I6 and permanent 4WD as Grenadier',
      'Extended wheelbase improves highway stability and ride quality',
      'Full off-road capability with beam axles and ladder-frame chassis',
    ],
    specs: [
      { label: 'Engine', value: '3.0L Twin-Turbo I6 (BMW)' },
      { label: 'Horsepower', value: '281 hp' },
      { label: '0-60 MPH', value: '9.0 seconds' },
      { label: 'Payload', value: '1,830 lbs' },
      { label: 'Seating', value: '5 passengers' },
      { label: 'Drivetrain', value: 'Permanent 4WD' },
    ],
    inventoryCount: 15,
    seo: {
      title: 'Used INEOS Quartermaster for Sale | BESPOKIAN Auto',
      description:
        'Shop BESPOKIAN-certified pre-owned INEOS Quartermaster pickup trucks. Purpose-built utility with genuine off-road capability.',
    },
  },
];
